import React, { useEffect, useState, useMemo, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import styles from './styles.module.css';
import PageHeader from '../../utils/PageHeader/PageHeader';
import { Grid } from '@mui/material';
import CustomTextField from '../../utils/CustomerComponents/CustomerTextFiled';
import CustomButton from '../../utils/CustomerComponents/CustomButton';
import {
  ORDER_TYPE,
  ROUTES,
  QUERY_PARAM_PIEFIX_KEY,
  QUERY_PARAM_TIMEOUT,
} from '../../../const';
import { useLocation } from 'react-router-dom';
import { handlePayment } from '../../utils/HandlePayment';
import { CartContext } from '../../../contexts/CartContext';
import { ShopContext } from '../../../contexts/ShopContext';
import { OrderContext } from '../../../contexts/OrderContext';
import Loader from '../../utils/Loading';
import CacheHelper from '../../../helpers/CacheHelper';
import { getQueryParamKey } from '../../utils/queryParamUtils';

function CustomerDetails() {
  const { cart, clearCartItems } = useContext(CartContext);
  const { shop } = useContext(ShopContext);
  const { countOrders } = useContext(OrderContext);

  const { search } = useLocation();

  const queryParams = useMemo(() => {
    const key = shop?.shopId ? getQueryParamKey(shop.shopId) : QUERY_PARAM_PIEFIX_KEY;
    const stored = localStorage.getItem(key);
    if (!stored) return {};

    try {
      const parsed = JSON.parse(stored);
      const isExpired = parsed.createdAt && Date.now() - parsed.createdAt > QUERY_PARAM_TIMEOUT;

      if (isExpired) {
        localStorage.removeItem(key);
        return {};
      }

      return parsed.data || {};
    } catch (err) {
      console.error('Invalid query param data', err);
      return {};
    }
  }, [shop?.shopId]);

  const history = useHistory();

  const [customer, setCustomer] = useState({});
  const [showLoading, setShowLoading] = useState(false);
  const [orderFields, setOrderFields] = useState(null);
  const [orderFieldsTobeCollected, setOrderFieldsTobeCollected] = useState([]);
  const [error, setError] = useState(null);

  const formValueChanged = (event) => {
    const { name, value } = event.target;
    setCustomer((prevCustomer) => ({
      ...prevCustomer,
      [name]: value,
    }));
  };

  const _placeOrder = async (_customer) => {
    await handlePayment({
      shop,
      cart,
      customerDetails: _customer || customer,
      setShowLoading,
      setError,
      history,
      onOrderPlaced: handleOrderPlacedWithoutPayment,
    });
  };

  const handleOrderPlacedWithoutPayment = (order) => {
    CacheHelper.saveOrderToLocalStorage(order, shop.shopId);
    clearCartItems(shop.shopId);
    countOrders();
    history.push(
      ROUTES.ORDER_DETAILS.replace(':shopId', shop.shopId).replace(":orderId", order.id)
    );
  };

  const placeOrderBtnPressed = async () => {
    setError(null);
    const _errors = [];

    const requiredFields = orderFields.filter((orderField) => orderField.required);
    for (const requiredField of requiredFields) {
      if (!customer[requiredField.id]) {
        _errors.push(`${requiredField.label} should not be empty`);
      }
    }
    if (_errors.length) {
      setError(_errors[0]);
      return;
    }

    await _placeOrder(customer);
  };

  useEffect(() => {
    if (!shop?.shopId) return;
    const customerDetails = CacheHelper.getCustomerFromLocalStorage(shop?.shopId)
    const mergedCustomer = { ...customerDetails, ...queryParams };
    const _orderType = new URLSearchParams(search).get('order_type') || mergedCustomer.order_type || 'default';
    const _orderFields = ORDER_TYPE[_orderType] || [];
    const _orderFieldsTobeCollected = _orderFields.filter((field) => {
      const existingValue = mergedCustomer[field.id];
      return (
        field.editable ||
        (field.required && !existingValue) ||
        existingValue
      );
    });
    setCustomer(mergedCustomer);
    setOrderFields(_orderFields);
    setOrderFieldsTobeCollected(_orderFieldsTobeCollected);
  }, [shop?.shopId, queryParams, search]);

  return (
    <>
      <Loader isOpen={showLoading} />
      <PageHeader title="Enter your basic details" />
      <div className={styles.container}>
        <Grid container>
          {orderFieldsTobeCollected.map((field) => (
            <>
              <Grid item xs={12} pt={2}>
                <span>
                  {field.label}
                  {field.required && <span>*</span>}
                </span>
              </Grid>
              <Grid item xs={12}>
                <CustomTextField
                  placeholder={field.placeholder ?? `Enter ${field.label}`}
                  name={field.id}
                  type={field.type}
                  onChange={formValueChanged}
                  value={customer[field.id] || ''}
                  disabled={!field.editable && !!customer[field.id]}
                />
              </Grid>
            </>
          ))}
          <Grid item xs={12} pt={2} className={styles.infoMessageContainer}>
            <span>Please collect your pickup order within 15 minutes.</span>
          </Grid>
          {error && (
            <Grid item xs={12} pt={2} className={styles.errorMessageContainer}>
              <span>{error}</span>
            </Grid>
          )}
          <Grid item xs={12} className={styles.btnContainer}>
            <CustomButton fullWidth onClick={placeOrderBtnPressed}>
              Continue
            </CustomButton>
          </Grid>
        </Grid>
      </div>
    </>
  );
}

export default CustomerDetails;
